import React from 'react'
import { useIntl } from 'react-intl'

import AddressWithViewMapButton from './Common/AddressWithViewMapButton'
import ConfirmationDetails from './Common/ConfirmationDetails'
import Divider from './Common/Divider'
import ItemTypeAndBookedLabel from './Common/ItemTypeAndBookedLabel'
import SummaryAdditionalNotes from './Common/SummaryAdditionalNotes'
import SummaryDataItem from './Common/SummaryDataItem'
import SummaryDateTime from './Common/SummaryDateTime'
import SummaryImage from './Common/SummaryImage'
import DefaultSummaryModalFooter from './Common/SummaryModalFooter/DefaultSummaryModalFooter'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import GooglePlaceRating from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRating'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { RestaurantBarItem } from 'tripPlanner/types/tripItem'
import { maybePlaceId } from 'tripPlanner/utils'

interface Props {
  item: RestaurantBarItem
}

function RestaurantBarItemSummary(props: Props) {
  const { item } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()
  const placeId = maybePlaceId(item.place)

  const reviewsLabel = intl.formatMessage({
    id: 'kSo6HR8Zi81QSN-X8emP',
    defaultMessage: 'Reviews',
    description: 'Label for the Google reviews section in restaurant/bar trip item summary',
  })
  const websiteLabel = intl.formatMessage({
    id: 'LpqAwxFnA28QtK9USyOq',
    defaultMessage: 'Website',
    description: 'Label for the website link in restaurant/bar trip item summary',
  })
  const phoneNumberLabel = intl.formatMessage({
    id: 'lQUFZKLX-XMGTfEtbaMa',
    defaultMessage: 'Phone number',
    description: 'Label for the phone number in restaurant/bar trip item summary',
  })

  return (
    <>
      <ModalHeader
        title={item.place.name}
        subtitle={item.place.region}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      <ModalBody>
        <SummaryImage
          place={item.place}
          tripId={item.tripId}
          tripItemId={item.id}
        />
        <ModalContent>
          <VerticalSpacer gap={20}>
            <ItemTypeAndBookedLabel item={item} />
            {(item.startDay || item.startDate || item.startTime) && (
              <SummaryDateTime
                date={item.startDate}
                time={item.startTime}
                day={item.startDay}
              />
            )}
            {item.place.address && (
              <AddressWithViewMapButton
                item={item}
                address={item.place.address}
              />
            )}
            {placeId && (
              <GooglePlaceRating size="M" placeId={placeId} label={reviewsLabel} />
            )}
            <ConfirmationDetails
              isBooked={item.isBooked}
              confirmationCode={item.confirmationCode}
              price={item.price}
            />
            <Divider />
            <SummaryDataItem title={websiteLabel} value={item.url} url={item.url} />
            <SummaryDataItem
              title={phoneNumberLabel}
              value={item.phone}
              url={`tel:${item.phone}`}
            />
            <SummaryAdditionalNotes notes={item.notes} />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>

      <DefaultSummaryModalFooter item={item} />
    </>
  )
}

export default RestaurantBarItemSummary
